import type { PlatformSubpageConfig } from '../types';
import { defaultCtas, localImage } from './shared';

const img = (src: string, alt: string) => ({
  src: localImage(src),
  alt,
  width: 1200,
  height: 800,
});

export const iotCloudConfig: PlatformSubpageConfig = {
  slug: 'iot-cloud',
  path: '/platform/iot-cloud',
  breadcrumbLabel: 'IoT Cloud',
  hero: {
    title: 'Enterprise IoT cloud for millions of connected devices',
    subtitle:
      'Real-time MQTT, device twins, and auto-scaling infrastructure built for production fleets.',
    ...defaultCtas,
  },
  heroImage: img('/images/home/feature-cloud.jpg', 'IoT cloud infrastructure dashboard'),
  eduIntro: {
    title: 'What is IoT cloud infrastructure?',
    description:
      'IoT cloud connects devices, applications, and analytics through secure messaging, state sync, and rules—so you can operate fleets at scale without building backend infrastructure from scratch.',
  },
  advantages: {
    eyebrow: 'COMPETITIVE EDGE',
    title: 'Cloud platform advantages for connected products',
    items: [
      { icon: 'cloud', title: 'Simplify Device-to-Cloud', description: 'Secure MQTT, REST APIs, and managed onboarding in minutes—not months.' },
      { icon: 'activity', title: 'Stable Performance', description: '99.99% uptime and sub-second message delivery at fleet scale.' },
      { icon: 'globe', title: 'Global Coverage', description: 'Multi-region deployment with low-latency sync worldwide.' },
    ],
  },
  featureShowcase: {
    eyebrow: 'CLOUD SERVICES',
    title: 'Manage device-to-cloud connectivity with confidence',
    subtitle: 'Explore how devices, cloud services, and apps stay connected.',
    items: [
      {
        title: 'MQTT infrastructure at scale',
        description: 'High-throughput pub/sub with TLS, topic ACLs, and per-tenant isolation for millions of devices.',
        image: img('/images/home/feature-cloud.jpg', 'MQTT infrastructure'),
        cta: { label: 'Learn more', to: '/contact-us' },
      },
      {
        title: 'Device twin architecture',
        description: 'Desired vs. reported state sync keeps apps, cloud rules, and firmware aligned in real time.',
        image: img('/images/home/partner-dashboard.png', 'Device twin'),
        cta: { label: 'Explore twins', to: '/contact-us' },
      },
      {
        title: 'Event processing pipelines',
        description: 'Stream device events into rules, webhooks, and analytics without custom plumbing.',
        image: img('/images/home/feature-firmware.jpg', 'Event processing'),
        cta: { label: 'View events', to: '/contact-us' },
      },
    ],
  },
  capabilities: {
    eyebrow: 'CAPABILITIES',
    title: 'Everything you need to run connected products',
    subtitle: 'Core cloud services for device fleets of any size.',
    items: [
      { icon: 'server', title: 'MQTT Infrastructure', description: 'High-throughput pub/sub with QoS and topic ACLs.' },
      { icon: 'refresh', title: 'Real-Time Device Sync', description: 'Keep apps, cloud, and firmware state aligned.' },
      { icon: 'layers', title: 'Device Twin Architecture', description: 'Desired vs. reported state with offline reconciliation.' },
      { icon: 'zap', title: 'Event Processing', description: 'Stream events into rules, webhooks, and analytics.' },
      { icon: 'activity', title: 'Auto Scaling', description: 'Elastic capacity that grows with your device fleet.' },
      { icon: 'globe', title: 'Multi-Region Support', description: 'Deploy closer to users with regional data paths.' },
    ],
  },
  ctaBanner: {
    title: 'Launch your connected product on enterprise-grade cloud today',
    ctaLabel: 'Get Started',
    ctaTo: '/contact-us',
    imageSrc: '/images/cta/solution-cta-robot.webp',
  },
  faqs: {
    title: 'Frequently asked questions',
    items: [
      { question: 'What protocols does the cloud support?', answer: 'MQTT, REST APIs, and WebSocket streams with TLS encryption and tenant isolation.' },
      { question: 'Can the platform scale with my fleet?', answer: 'Yes—auto-scaling infrastructure supports millions of devices with sub-second delivery.' },
      { question: 'Is multi-region deployment available?', answer: 'Deploy across regions for lower latency and data residency requirements.' },
    ],
  },
  sections: {
    eyebrow: 'ARCHITECTURE',
    title: 'Standard IoT device-to-cloud architecture',
    subtitle: 'Explore how devices, cloud services, and apps stay connected.',
  },
};

export const iotCloudTabs = [
  {
    id: 'mqtt',
    label: 'MQTT',
    title: 'MQTT Infrastructure',
    description:
      'High-throughput pub/sub messaging with TLS, topic ACLs, and per-tenant isolation for millions of devices.',
    points: ['Sub-second delivery', 'QoS 0/1/2 support', 'Persistent sessions'],
  },
  {
    id: 'twin',
    label: 'Device Twin',
    title: 'Device Twin Architecture',
    description:
      'Desired vs. reported state sync keeps apps, cloud rules, and firmware aligned in real time.',
    points: ['Shadow documents', 'Conflict resolution', 'Offline reconciliation'],
  },
  {
    id: 'events',
    label: 'Events',
    title: 'Event Processing',
    description:
      'Stream device events into rules, webhooks, and analytics pipelines without custom plumbing.',
    points: ['Rule engine hooks', 'Webhook fan-out', 'Batch analytics export'],
  },
] as const;
